import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { apiFetch, ApiProblem, newIdempotencyKey, type BinLocationView, type Manufacturer, type SupplierOption } from '../lib/api.ts';
import type { ComboOption } from '../lib/combobox.ts';
import { countryOptions, currencyOptions, FREQUENT_COUNTRIES, FREQUENT_CURRENCIES } from '../lib/combobox-options.ts';
import { useDebounced, useGroupSearch } from '../lib/combobox-search.ts';
import { useSession } from '../lib/session.tsx';
import { useArticleFacts } from './article-hits.tsx';
import { Combobox, type ComboboxProps } from './ui/combobox.tsx';

/**
 * Varianten der Auswahl mit Suche (Ticket 1.257, DESIGN „Auswahl mit Suche“) als dünne Hüllen über die bestehenden
 * Lese-Endpunkte: Land, Währung, Kunde, Artikel, Lieferant, Lagerplatz, Hersteller. Gespeichert wird der technische
 * Wert (ISO-Kürzel, id, Lagerplatz-Kürzel), angezeigt der Name. Keine eigene Schreiblogik außer „Hersteller neu
 * anlegen“ über den bestehenden Command (`POST /api/v1/catalog/manufacturers`).
 */

/** Gemeinsame Eingaben jeder Variante (Optionen und Liste setzt die Variante selbst). */
export type VariantProps = Omit<ComboboxProps, 'options' | 'label' | 'frequent' | 'remote' | 'loading' | 'loadError' | 'onCreate'> & {
  /** Name der Liste für Hilfsmittel und Blatt-Überschrift; Standard je Variante. */
  label?: string;
};

/** Land (ISO 3166-1 alpha-2): „Österreich · AT“, oben Deutschland, Österreich, Schweiz. */
export function CountryCombobox({ label = 'Land', width = 'm', ...props }: VariantProps) {
  return <Combobox {...props} label={label} width={width} options={countryOptions()} frequent={FREQUENT_COUNTRIES} />;
}

/** Währung (ISO 4217): „CHF – Schweizer Franken“, oben EUR, CHF, USD, GBP. */
export function CurrencyCombobox({ label = 'Währung', width = 'm', ...props }: VariantProps) {
  return <Combobox {...props} label={label} width={width} options={currencyOptions()} frequent={FREQUENT_CURRENCIES} />;
}

/** Kunde (Nummer + Name, darunter Ort · Land) über die globale Suche; Wert = id des Kunden. */
export function CustomerCombobox({ label = 'Kunde', width = 'l', ...props }: VariantProps) {
  const [q, setQ] = useState('');
  const search = useGroupSearch('customers', q);
  const options = useMemo<ComboOption[]>(() => search.hits.map((h) => ({ value: h.id, label: h.title, hint: h.subtitle })), [search.hits]);
  return (
    <Combobox
      {...props}
      label={label}
      width={width}
      options={options}
      loading={search.loading}
      loadError={search.error}
      remote={{ onQuery: setQ, tooShort: search.tooShort, more: search.more }}
    />
  );
}

/** Auftrag (Nummer, darunter Kunde) über die globale Suche; Wert = id des Auftrags (Story 23.1, Aufgabe am Auftrag). */
export function OrderCombobox({ label = 'Auftrag', width = 'l', ...props }: VariantProps) {
  const [q, setQ] = useState('');
  const search = useGroupSearch('orders', q);
  const options = useMemo<ComboOption[]>(() => search.hits.map((h) => ({ value: h.id, label: h.title, hint: h.subtitle })), [search.hits]);
  return (
    <Combobox
      {...props}
      label={label}
      width={width}
      options={options}
      loading={search.loading}
      loadError={search.error}
      remote={{ onQuery: setQ, tooShort: search.tooShort, more: search.more }}
    />
  );
}

/** Artikel (TT-Nummer · Name, darunter Bestand) über die globale Suche; Wert = id des Artikels. */
export function ArticleCombobox({ label = 'Artikel', width = 'l', ...props }: VariantProps) {
  const [q, setQ] = useState('');
  const search = useGroupSearch('articles', q);
  // Bestand je Treffer in einer Anfrage (ohne Preise); fehlt er, steht nur Nummer und Name.
  const facts = useArticleFacts(
    search.hits.length === 0 ? null : { lines: search.hits.map((h) => ({ productNumber: h.title, quantity: 1 })), customerId: null, currency: null, orderId: null, prices: false },
  );
  const options = useMemo<ComboOption[]>(
    () =>
      search.hits.map((h, i) => ({
        value: h.id,
        label: h.subtitle ? `${h.title} · ${h.subtitle}` : h.title,
        keys: [h.title],
        hint: facts.data?.lines.find((l) => l.index === i && l.productNumber === h.title)?.lightText ?? null,
      })),
    [search.hits, facts.data],
  );
  return (
    <Combobox
      {...props}
      label={label}
      width={width}
      options={options}
      loading={search.loading}
      loadError={search.error}
      remote={{ onQuery: setQ, tooShort: search.tooShort, more: search.more }}
    />
  );
}

/** Höchstzahl Lieferanten je Antwort des Endpunkts (`SUPPLIER_OPTIONS_LIMIT` in purchasing; technische Grenze). */
const SUPPLIER_LIMIT = 200;

/**
 * Lieferant (Nummer · Name, darunter Standardwährung); Server filtert Nummer und Name. Wert = id des Lieferanten.
 * `exclude` blendet Lieferanten aus (z. B. schon am Artikel); `onSupplier` liefert den gewählten Lieferanten (z. B. für
 * den Währungsvorschlag).
 */
export function SupplierCombobox({
  label = 'Lieferant',
  width = 'l',
  exclude,
  onSupplier,
  ...props
}: VariantProps & { exclude?: readonly string[]; onSupplier?: (supplier: SupplierOption | null) => void }) {
  const { getToken } = useSession();
  const [q, setQ] = useState('');
  const debounced = useDebounced(q.trim());
  const suppliers = useQuery({
    queryKey: ['supplier-options', debounced],
    queryFn: () => apiFetch<{ items: SupplierOption[] }>(getToken, `/api/v1/purchasing/suppliers${debounced ? `?q=${encodeURIComponent(debounced)}` : ''}`),
  });
  const waiting = q.trim() !== debounced || suppliers.isFetching;
  const items = useMemo(
    () => (waiting ? [] : (suppliers.data?.items ?? []).filter((s) => !exclude?.includes(s.id) || s.id === props.value)),
    [waiting, suppliers.data, exclude, props.value],
  );
  const options = useMemo<ComboOption[]>(
    () => items.map((s) => ({ value: s.id, label: `${s.number} · ${s.name}`, keys: [s.number, s.name], hint: `Währung ${s.defaultCurrency}` })),
    [items],
  );
  return (
    <Combobox
      {...props}
      onChange={(value, option) => {
        props.onChange(value, option);
        onSupplier?.(value === null ? null : (items.find((s) => s.id === value) ?? null));
      }}
      label={label}
      width={width}
      options={options}
      loading={waiting}
      loadError={!waiting && suppliers.isError}
      remote={{ onQuery: setQ, more: items.length >= SUPPLIER_LIMIT }}
    />
  );
}

/** Text der leeren Lagerplatz-Option (Wert ''). */
export const NO_BIN_LABEL = 'kein Lagerplatz';

/**
 * Lagerplatz (Kürzel, darunter Bereich); gesperrte Plätze nur, wenn sie der aktuelle Wert sind. Wert = Kürzel. Mit
 * `onCommit` sofort speichern samt „Rückgängig“. `withEmpty` stellt „kein Lagerplatz“ (Wert '') an den Anfang; leer
 * zeigt das Feld dann denselben Text.
 */
export function BinCombobox({ label = 'Lagerplatz', width = 'm', withEmpty = false, ...props }: VariantProps & { withEmpty?: boolean }) {
  const { getToken } = useSession();
  const bins = useQuery({
    queryKey: ['inventory', 'bins'],
    queryFn: () => apiFetch<{ items: BinLocationView[] }>(getToken, '/api/v1/inventory/bin-locations'),
  });
  const value = props.value;
  const options = useMemo<ComboOption[]>(
    () => [
      ...(withEmpty ? [{ value: '', label: NO_BIN_LABEL }] : []),
      ...(bins.data?.items ?? [])
        .filter((b) => !b.locked || b.code === value)
        .map((b) => ({ value: b.code, label: b.code, keys: b.area ? [b.area] : [], hint: b.area })),
    ],
    [bins.data, value, withEmpty],
  );
  return (
    <Combobox
      placeholder={withEmpty ? NO_BIN_LABEL : undefined}
      {...props}
      // Leer = Option „kein Lagerplatz“ (Haken in der Liste, kein Speichern beim erneuten Wählen).
      value={withEmpty ? (props.value ?? '') : props.value}
      label={label}
      width={width}
      options={options}
      loading={bins.isPending}
      loadError={bins.isError}
    />
  );
}

/**
 * Hersteller (Name) mit „+ „<Text>“ neu anlegen“ als letzter Zeile; der neue Hersteller ist danach gewählt. Wert = id.
 * `emptyLabel` stellt eine leere Option (Wert '') an den Anfang, z. B. „kein Hersteller“ zum Entfernen.
 */
export function ManufacturerCombobox({ label = 'Hersteller', width = 'l', emptyLabel, ...props }: VariantProps & { emptyLabel?: string }) {
  const { getToken } = useSession();
  const queryClient = useQueryClient();
  const makers = useQuery({
    queryKey: ['manufacturers'],
    queryFn: async () => (await apiFetch<{ items: Manufacturer[] }>(getToken, '/api/v1/catalog/manufacturers')).items,
  });
  const options = useMemo<ComboOption[]>(
    () => [...(emptyLabel ? [{ value: '', label: emptyLabel }] : []), ...(makers.data ?? []).map((m) => ({ value: m.id, label: m.name }))],
    [makers.data, emptyLabel],
  );
  async function create(name: string): Promise<ComboOption> {
    try {
      const m = await apiFetch<Manufacturer>(getToken, '/api/v1/catalog/manufacturers', { method: 'POST', body: { name }, idempotencyKey: newIdempotencyKey() });
      await queryClient.invalidateQueries({ queryKey: ['manufacturers'] });
      // Stammdaten-Seite (Reiter Hersteller) frisch halten.
      void queryClient.invalidateQueries({ queryKey: ['catalog-master-data'] });
      return { value: m.id, label: m.name };
    } catch (err) {
      throw new Error(err instanceof ApiProblem ? (err.errors.name ?? err.detail) : 'Etwas ist schiefgelaufen. Bitte noch einmal versuchen.', { cause: err });
    }
  }
  return (
    <Combobox
      placeholder={emptyLabel}
      {...props}
      value={emptyLabel ? (props.value ?? '') : props.value}
      onChange={(value, option) => props.onChange(value === '' ? null : value, option)}
      label={label}
      width={width}
      options={options}
      loading={makers.isPending}
      loadError={makers.isError}
      onCreate={create}
    />
  );
}
